import { useState, useEffect, useCallback, useRef } from 'react';
import CallService from '../services/CallService';

export default function useCallPermission(contactId, phoneNumber) {
  const [state, setState] = useState({ loading: true });
  const [sendingRequest, setSendingRequest] = useState(false);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const checkPermission = useCallback(async () => {
    if (!contactId && !phoneNumber) {
      const empty = { loading: false };
      setState(empty);
      return empty;
    }

    setState((prev) => ({ ...prev, loading: true }));

    try {
      const raw = await CallService.getCallPermissionState(contactId, phoneNumber);
      const response = raw?.data ?? raw;

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response from server');
      }

      const nextState = {
        loading: false,
        hasPermission: response.hasPermission,
        canCall: response.canCall,
        canSendRequest: response.canSendRequest,
        permissionStatus: response.permissionStatus,
        expiresAt: response.expiresAt,
        showCallButton: response.showCallButton,
        showRequestButton: response.showRequestButton,
        unavailableReason: response.unavailableReason,
        requestRemaining24h: response.requestRemaining24h,
        requestRemaining7d: response.requestRemaining7d,
        error: null,
      };
      setState(nextState);
      return nextState;
    } catch (error) {
      console.error('Failed to get call permission state:', error);
      const errorState = {
        loading: false,
        error: error.message || 'Failed to load call permission.',
      };
      setState(errorState);
      return errorState;
    }
  }, [contactId, phoneNumber]);

  const sendRequest = useCallback(async (customMessage = null) => {
    if (stateRef.current.canSendRequest !== true) {
      return {
        success: false,
        error: 'Cannot send permission request at this time',
      };
    }

    setSendingRequest(true);

    try {
      const response = await CallService.sendPermissionRequest(contactId, phoneNumber, customMessage);

      let pollCount = 0;
      const maxPolls = 20;

      const pollInterval = setInterval(async () => {
        pollCount += 1;
        const updated = await checkPermission();

        if (updated?.hasPermission === true || pollCount >= maxPolls) {
          clearInterval(pollInterval);
        }
      }, 3000);

      setTimeout(() => clearInterval(pollInterval), 60000);

      return { success: true, data: response };
    } catch (error) {
      console.error('Failed to send permission request:', error);
      return {
        success: false,
        error: error.response?.data?.error?.message || error.message,
      };
    } finally {
      setSendingRequest(false);
    }
  }, [checkPermission, contactId, phoneNumber]);

  const refreshPermission = useCallback(async () => {
    await checkPermission();
  }, [checkPermission]);

  useEffect(() => {
    checkPermission();
  }, [contactId, phoneNumber, checkPermission]);

  return {
    ...state,
    sendRequest,
    sendingRequest,
    refreshPermission,
  };
}
